// src/components/Majanga.js
import React, { useState, useEffect } from "react";
import {
  Plus, Search, Users, Eye, Edit, Trash2, FileText, ArrowLeft,
  User, Check, X, ChevronDown, ChevronUp, Upload, Download,
  CheckCircle, XCircle, Filter, Shield, Settings, AlertTriangle,
  BarChart2, ChevronRight, Layers, Activity, TrendingUp, Award,
  Bell, RefreshCw, Calendar, MapPin, Mail, Crown, BookOpen,
  DollarSign as DollarIcon, Star, Zap, Clock, ArrowRight,
} from "lucide-react";
import { MajangaService } from "./majanga/MajangaService";
import MajangaConfiguration from "./Majangaconfiguration";

// ─── Brand ────────────────────────────────────────────────────────────────────
const NAV = "#001f3f";
const TEAL = "#0d9488";

// ─── Role icons ───────────────────────────────────────────────────────────────
const ROLE_ICON = {
  chairperson: Crown,
  secretary:   BookOpen,
  treasurer:   DollarIcon,
  vice_chair:  Star,
  member:      User,
  custom:      Zap,
  admin:       Shield,
};
const ROLE_COLOR = {
  chairperson: "bg-amber-100 text-amber-700",
  secretary:   "bg-blue-100 text-blue-700",
  treasurer:   "bg-green-100 text-green-700",
  vice_chair:  "bg-purple-100 text-purple-700",
  member:      "bg-slate-100 text-slate-600",
  custom:      "bg-teal-100 text-teal-700",
  admin:       "bg-rose-100 text-rose-700",
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    approved:     "bg-emerald-100 text-emerald-700 border border-emerald-200",
    rejected:     "bg-red-100 text-red-700 border border-red-200",
    pending:      "bg-amber-100 text-amber-700 border border-amber-200",
    submitted:    "bg-blue-100 text-blue-700 border border-blue-200",
    draft:        "bg-slate-100 text-slate-600 border border-slate-200",
    under_review: "bg-violet-100 text-violet-700 border border-violet-200",
    skipped:      "bg-slate-100 text-slate-500 border border-slate-200",
    accepted:     "bg-emerald-100 text-emerald-700 border border-emerald-200",
    active:       "bg-teal-100 text-teal-700 border border-teal-200",
    inactive:     "bg-slate-100 text-slate-600 border border-slate-200",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide capitalize ${map[status?.toLowerCase()] || map.draft}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
};

// Every section the CFN/joining form actually collects - used everywhere a
// member's full details are shown, so nothing filled in during registration
// stays invisible (kin/emergency contacts and medical info were previously
// left out entirely).
const memberDetailSections = (m) => [
  { title: "Personal", data: [
    ["Full Name", m.full_name], ["Phone", m.phone_number], ["Email", m.email],
    ["Gender", m.gender], ["Marital Status", m.marital_status], ["Spouse", m.spouse_name],
    ["Date of Birth", m.date_of_birth ? new Date(m.date_of_birth).toLocaleDateString() : null],
  ]},
  { title: "Address", data: [
    ["Current", m.current_address], ["Permanent", m.permanent_address], ["Business", m.business_address],
  ]},
  { title: "Professional", data: [
    ["Occupation", m.occupation], ["Employer", m.employer], ["Work Address", m.work_address],
    ["Monthly Income", m.monthly_income ? `TZS ${parseFloat(m.monthly_income).toLocaleString()}` : null],
  ]},
  { title: "Family", data: [
    ["Father", m.father_name], ["Mother", m.mother_name],
    ["Father-in-Law", m.father_in_law_name || m.father_in_law],
    ["Mother-in-Law", m.mother_in_law_name],
    ["Children", m.number_of_children], ["Children Names", m.children_names],
  ]},
  { title: "Next of Kin / Emergency", data: [
    ["Next of Kin", m.next_of_kin], ["Kin Phone", m.next_of_kin_phone], ["Relationship", m.next_of_kin_relationship],
    ["Emergency Contact", m.emergency_contact_name], ["Emergency Phone", m.emergency_contact_phone],
  ]},
  { title: "Medical", data: [
    ["Medical Checkup", m.medical_check_up || m.medical_checkup],
    ["Financed by ENF", m.medical_check_up_financed_by_enf ? "Yes" : "No"],
    ["Health Insurance", m.has_health_insurance ? "Yes" : "No"],
    ["Insurance Provider", m.health_insurance_provider],
  ]},
  { title: "Contribution", data: [
    ["Amount 1", m.contribution_amount1 ? `TZS ${parseFloat(m.contribution_amount1).toLocaleString()}` : null],
    ["Amount 2", m.contribution_amount2 ? `TZS ${parseFloat(m.contribution_amount2).toLocaleString()}` : null],
    ["Total", m.contribution_total ? `TZS ${parseFloat(m.contribution_total).toLocaleString()}` : null],
    ["Expected Monthly", m.expected_monthly_contribution ? `TZS ${parseFloat(m.expected_monthly_contribution).toLocaleString()}` : null],
  ]},
  { title: "Banking", data: [
    ["Bank", m.bank_name], ["Account No.", m.account_number], ["Account Name", m.account_name],
  ]},
  { title: "Witness", data: [
    ["Name", m.witness_name], ["Title", m.witness_designation], ["Phone", m.witness_phone],
  ]},
];

// Shared block: photo (if any) + every section above, in the standard
// 2/3-col info-card grid already used across these admin pages.
const MemberFullDetails = ({ m }) => (
  <div>
    {m.profile_image_url && (
      <div className="flex items-center gap-3 mb-3">
        <img src={m.profile_image_url} alt={m.full_name || "Member"} className="w-16 h-16 rounded-xl object-cover border border-slate-200" />
        <span className="text-xs text-slate-400">Photo attached during registration</span>
      </div>
    )}
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {memberDetailSections(m).map(s => (
        <div key={s.title} className="bg-white rounded-xl p-3.5 border border-slate-100">
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 pb-1 border-b border-slate-100">{s.title}</p>
          {s.data.map(([l, v]) => (
            <div key={l} className="flex justify-between gap-2 py-0.5">
              <span className="text-xs text-slate-400">{l}</span>
              <span className="text-xs font-medium text-slate-700 text-right truncate max-w-[140px]">{v || "N/A"}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color = "teal" }) => {
  const colors = {
    teal:  "from-teal-500 to-teal-600",
    navy:  "from-[#001f3f] to-[#002d5a]",
    gold:  "from-amber-400 to-amber-500",
    blue:  "from-blue-500 to-blue-600",
  };
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center shadow-md flex-shrink-0`}>
        <Icon size={22} className="text-white" />
      </div>
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-slate-800 leading-tight">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

// ─── Approval chain progress bar ──────────────────────────────────────────────
const ApprovalChainProgress = ({ chain = [], currentStep, chainStatus }) => {
  if (!chain.length) return null;

  const statusIcon = (decision) => {
    if (decision === "approved") return <Check size={11} className="text-white" />;
    if (decision === "rejected") return <X size={11} className="text-white" />;
    if (decision === "skipped")  return <ArrowRight size={11} className="text-white" />;
    return null;
  };
  const stepBg = (step) => {
    if (step.decision === "approved") return "bg-emerald-500";
    if (step.decision === "rejected") return "bg-red-500";
    if (step.decision === "skipped")  return "bg-slate-400";
    if (step.step_number === currentStep) return "bg-amber-400 animate-pulse";
    return "bg-slate-200";
  };
  const lineBg = (step, i) => {
    if (i >= chain.length - 1) return "bg-slate-200";
    return step.decision === "approved" ? "bg-emerald-400" : "bg-slate-200";
  };

  return (
    <div className="mt-2">
      <div className="flex items-center gap-0">
        {chain.map((step, i) => {
          const RoleIcon = ROLE_ICON[step.gang_role?.role_type || "admin"] || Shield;
          const isCurrent = step.step_number === currentStep && !step.decision;
          return (
            <React.Fragment key={step.id || i}>
              {/* Step circle */}
              <div className="flex flex-col items-center gap-1" style={{ minWidth: 48 }}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${stepBg(step)}`}
                  title={step.gang_role_name || "Admin"}>
                  {step.decision
                    ? statusIcon(step.decision)
                    : <RoleIcon size={12} className={isCurrent ? "text-white" : "text-slate-400"} />
                  }
                </div>
                <span className="text-[9px] text-slate-500 text-center leading-tight max-w-[44px] truncate">
                  {step.gang_role_name || "Admin"}
                </span>
              </div>
              {/* Connector */}
              {i < chain.length - 1 && (
                <div className={`h-0.5 flex-1 -mt-4 ${lineBg(step, i)}`} style={{ minWidth: 12 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ─── Tab button ───────────────────────────────────────────────────────────────
const Tab = ({ icon: Icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
      active
        ? "border-teal-500 text-teal-600"
        : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
    }`}
  >
    <Icon size={15} />
    <span>{label}</span>
    {badge > 0 && (
      <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
        {badge}
      </span>
    )}
  </button>
);

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const Majanga = () => {
  const [activeView, setActiveView]   = useState("dashboard");
  const [gangs, setGangs]             = useState([]);
  const [cfnform, setCfnform]         = useState({ results: [] });
  const [gangRequests, setGangRequests] = useState([]);
  const [requestStatusFilter, setRequestStatusFilter] = useState("all");
  const [gangDetails, setGangDetails] = useState(null);
  const [gangMemberRoles, setGangMemberRoles] = useState([]); // roles for current expanded gang
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [searchTerm, setSearchTerm]   = useState("");
  const [selectedRequest, setSelectedRequest]     = useState(null);
  const [deletingFormId, setDeletingFormId]       = useState(null);
  const [selectedCFNForm, setSelectedCFNForm]     = useState(null);
  const [expandedMemberId, setExpandedMemberId]   = useState(null);
  const [showContributionModal, setShowContributionModal] = useState(false);
  const [contributionHistory, setContributionHistory]     = useState(null);
  const [showHistoryModal, setShowHistoryModal]           = useState(false);
  const [sponsorForms, setSponsorForms]     = useState([]);
  const [sponsorRequests, setSponsorRequests] = useState([]);
  const [selectedSponsorRequest, setSelectedSponsorRequest] = useState(null);
  const [sponsorSearchTerm, setSponsorSearchTerm] = useState("");
  const [sponsorStatusFilter, setSponsorStatusFilter] = useState("all");
  // Approval chains cache  { requestId: [...steps] }
  const [approvalChains, setApprovalChains] = useState({});
  const [loadingChain, setLoadingChain]     = useState(null);
  const [serviceRequests, setServiceRequests] = useState([]);

  // ── Approve membership modal ───────────────────────────────────────────────
  const [approveModal, setApproveModal]         = useState(null); // { request, gangRoles }
  const [approveModalLoading, setApproveModalLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId]     = useState("");   // role to assign on approve
  const [approveNotes, setApproveNotes]         = useState("");

  const [newGang, setNewGang] = useState({
    name: "", description: "", location: "", is_private: false,
    contact_email: "", contact_phone: "", joining_fee: "", specification: "All",
  });
  const [editGang, setEditGang] = useState({ id: null });
  const [newContribution, setNewContribution] = useState({
    amount: "", description: "", memberId: null, gangId: null,
    contribution_date: null, is_joining_fee: false,
  });
  const [newSponsorForm, setNewSponsorForm] = useState({
    title: "", description: "", version: "", is_active: true, form_pdf: null,
  });

  // ── Tab definitions ────────────────────────────────────────────────────────
  const tabs = [
    { id: "dashboard",       label: "Dashboard",        icon: BarChart2 },
    { id: "gangs",           label: "Communities",      icon: Layers },
    { id: "members",         label: "Memberships",      icon: Users },
    { id: "contributions",   label: "Contributions",    icon: TrendingUp },
    { id: "individual",      label: "Individual CFN",   icon: FileText },
    { id: "requests",        label: "Join Requests",    icon: Bell,   badge: gangRequests.filter(r => r.status === "pending").length },
    { id: "serviceRequests", label: "Service Requests", icon: Shield, badge: 0 },
    { id: "sponsorForms",    label: "Sponsor Forms",    icon: Upload },
    { id: "sponsorRequests", label: "Sponsor Requests", icon: Award },
    { id: "configuration",   label: "Configuration",    icon: Settings },
  ];

  // ── Data loaders ───────────────────────────────────────────────────────────
  useEffect(() => { fetchGangs(); }, []);
  useEffect(() => { if (activeView === "requests") fetchGangRequests(); }, [activeView]);
  useEffect(() => { if (activeView === "serviceRequests") fetchServiceRequests(); }, [activeView]);
  useEffect(() => { if (activeView === "sponsorForms") fetchSponsorForms(); }, [activeView]);
  useEffect(() => {
    if (activeView === "sponsorRequests")
      fetchSponsorRequests(sponsorStatusFilter === "all" ? null : sponsorStatusFilter);
  }, [activeView, sponsorStatusFilter]);

  const fetchGangs = async () => {
    setLoading(true);
    try {
      const [data, cfndata] = await Promise.all([
        MajangaService.getAllGangs(),
        MajangaService.getIndividualForms(),
      ]);
      setGangs(data);
      setCfnform(cfndata || { results: [] });
      setError(null);
    } catch { setError("Failed to load communities."); }
    finally { setLoading(false); }
  };

  const fetchGangDetails = async (gangId) => {
    setLoading(true);
    setGangMemberRoles([]); // clear previous gang's roles
    try {
      const [data, rolesData] = await Promise.all([
        MajangaService.getGangDetails(gangId),
        MajangaService.getMemberRolesByGang(gangId).catch(() => []),
      ]);
      setGangDetails(data);
      // rolesData: array of { membership, membership_name, role, role_name, role_type, ... }
      const rolesArr = rolesData.results || rolesData || [];
      setGangMemberRoles(rolesArr);
      setError(null);
    } catch { setError("Failed to load community details."); }
    finally { setLoading(false); }
  };

  // A rejected membership is not a member — keep them out of the gang's member lists.
  const activeMembers = (members) => (members || []).filter(m => m.status !== "rejected");

  // Helper: get roles for a membership_id
  const getMemberRoles = (membershipId) =>
    gangMemberRoles.filter(r => r.membership === membershipId && r.is_active !== false);

  const fetchGangRequests = async () => {
    setLoading(true);
    try {
      const data = await MajangaService.getGangRequest();
      const arr  = data.results || data;
      setGangRequests(arr.map(item => ({
        id:         item.id,
        user_name:  item.user?.name  || item.full_name || "Unknown",
        user_email: item.user?.email || item.email     || "No Email",
        gang_id:    item.gang        || item.gang_details?.id,
        gang_name:  item.gang_details?.name || "Unknown",
        status:     item.status || "pending",
        created_at: item.join_date || item.joining_date || new Date().toISOString(),
        ...item,
      })));
      setError(null);
    } catch { setError("Failed to load requests."); }
    finally { setLoading(false); }
  };

  // ── Service requests with approval chains ──────────────────────────────────

  const fetchServiceRequests = async () => {
    setLoading(true);
    try {
      const data = await MajangaService.getAllServiceRequests();
      const arr = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      setServiceRequests(arr);
      setError(null);
    } catch { setError("Failed to load service requests."); }
    finally { setLoading(false); }
  };

  const fetchApprovalChain = async (requestId) => {
    if (approvalChains[requestId]) return; // already cached
    setLoadingChain(requestId);
    try {
      const data = await MajangaService.getServiceRequestApprovalChain(requestId);
      setApprovalChains(prev => ({
        ...prev,
        [requestId]: data.approval_steps || data.chain || [],
      }));
    } catch { /* silently fail */ }
    finally { setLoadingChain(null); }
  };

  const fetchContributionHistory = async (membershipId) => {
    setLoading(true);
    try {
      const data = await MajangaService.getMemberContributionHistory(membershipId);
      setContributionHistory(data);
      setError(null);
    } catch { setError("Failed to load contribution history."); }
    finally { setLoading(false); }
  };

  const fetchSponsorForms = async () => {
    setLoading(true);
    try {
      const data = await MajangaService.getActiveForms();
      setSponsorForms(data.forms || []);
      setError(null);
    } catch { setError("Failed to load sponsor forms."); }
    finally { setLoading(false); }
  };

  const fetchSponsorRequests = async (status = null) => {
    setLoading(true);
    try {
      const data = await MajangaService.getAllSponsorRequests(status);
      setSponsorRequests(data.requests || []);
      setError(null);
    } catch { setError("Failed to load sponsor requests."); }
    finally { setLoading(false); }
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleAddGang = async () => {
    setLoading(true);
    try {
      const created = await MajangaService.createGang(newGang);
      setGangs([...gangs, { ...created, members: 0, contributions: 0 }]);
      setNewGang({ name: "", description: "", location: "", is_private: false, contact_email: "", contact_phone: "", joining_fee: "", specification: "All" });
      setActiveView("gangs");
      setError(null);
    } catch { setError("Failed to create community."); }
    finally { setLoading(false); }
  };

  const handleDeleteGang = async (id) => {
    if (!window.confirm("Delete this community?")) return;
    setLoading(true);
    try {
      await MajangaService.deleteGang(id);
      setGangs(gangs.filter(g => g.id !== id));
      setError(null);
    } catch { setError("Failed to delete community."); }
    finally { setLoading(false); }
  };

  const handleUpdateGang = async () => {
    setLoading(true);
    try {
      const updated = await MajangaService.updateGang(editGang.id, editGang);
      setGangs(gangs.map(g => g.id === editGang.id ? { ...g, ...updated } : g));
      setEditGang({ id: null });
      setError(null);
    } catch { setError("Failed to update community."); }
    finally { setLoading(false); }
  };

  // Open the approve modal — fetches gang roles first so admin can pick one
  const openApproveModal = async (request) => {
    setApproveModalLoading(true);
    setSelectedRoleId("");
    setApproveNotes("");
    try {
      const roles = await MajangaService.getGangRoles(request.gang_id || request.gang);
      const rolesArr = roles.results || roles || [];
      setApproveModal({ request, gangRoles: rolesArr });
    } catch {
      // Fallback: open with no roles
      setApproveModal({ request, gangRoles: [] });
    } finally {
      setApproveModalLoading(false);
    }
  };

  const handleApproveRequest = async (requestId, type) => {
    setLoading(true);
    try {
      await MajangaService.approveGangRequest(requestId, { status: type });
      setGangRequests(gangRequests.map(r => r.id === requestId ? { ...r, status: type } : r));
      if (selectedRequest?.id === requestId) setSelectedRequest({ ...selectedRequest, status: type });
      setError(null);
    } catch { setError("Failed to process request."); }
    finally { setLoading(false); }
  };

  // Approve + optionally assign role in one action
  const handleConfirmApprove = async () => {
    if (!approveModal) return;
    const { request } = approveModal;
    setLoading(true);
    try {
      // 1. Approve the membership
      await MajangaService.approveGangRequest(request.id, { status: "approved" });
      setGangRequests(gangRequests.map(r => r.id === request.id ? { ...r, status: "approved" } : r));
      if (selectedRequest?.id === request.id) setSelectedRequest({ ...selectedRequest, status: "approved" });

      // 2. Assign role if admin chose one
      if (selectedRoleId && request.membership_id) {
        try {
          await MajangaService.assignRoleToMember(parseInt(selectedRoleId), request.membership_id, approveNotes);
        } catch (roleErr) {
          // Role assignment failed — membership still approved, just warn
          setError("Member approved but role assignment failed. You can assign the role from Configuration.");
        }
      }

      setApproveModal(null);
      setError(null);
    } catch { setError("Failed to approve membership."); }
    finally { setLoading(false); }
  };

  const handleRejectRequest = async (requestId) => {
    setLoading(true);
    try {
      await MajangaService.approveGangRequest(requestId, { status: "rejected" });
      setGangRequests(gangRequests.map(r => r.id === requestId ? { ...r, status: "rejected" } : r));
      if (selectedRequest?.id === requestId) setSelectedRequest({ ...selectedRequest, status: "rejected" });
      setError(null);
    } catch { setError("Failed to reject request."); }
    finally { setLoading(false); }
  };

  // Rejected requests otherwise sit around forever with no way to clear
  // them out - this lets an admin remove one permanently once it's no
  // longer wanted.
  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Permanently delete this rejected request? This cannot be undone.")) return;
    setLoading(true);
    try {
      await MajangaService.deleteGangRequest(requestId);
      setGangRequests(gangRequests.filter(r => r.id !== requestId));
      if (selectedRequest?.id === requestId) { setSelectedRequest(null); setActiveView("requests"); }
      setError(null);
    } catch { setError("Failed to delete request."); }
    finally { setLoading(false); }
  };

  const handleAddContribution = async () => {
    if (!newContribution.amount || isNaN(newContribution.amount)) { setError("Valid amount required"); return; }
    setLoading(true);
    try {
      await MajangaService.addContribution({
        membership:          newContribution.memberId,
        amount:              parseFloat(newContribution.amount),
        description:         newContribution.description,
        contribution_date:   new Date().toISOString().split("T")[0],
        is_joining_fee:      false,
      });
      await fetchGangDetails(newContribution.gangId);
      setNewContribution({ amount: "", description: "", memberId: null, gangId: null, contribution_date: null, is_joining_fee: false });
      setShowContributionModal(false);
      setError(null);
    } catch { setError("Failed to add contribution."); }
    finally { setLoading(false); }
  };

  const handleDeleteCFNForm = async (formId, formName) => {
    if (!window.confirm(`Delete CFN form for "${formName}"?`)) return;
    setDeletingFormId(formId);
    setLoading(true);
    try {
      await MajangaService.deleteCFNForm(formId);
      setCfnform(prev => ({ ...prev, results: prev.results.filter(f => f.id !== formId) }));
      setError(null);
    } catch { setError("Failed to delete CFN form."); }
    finally { setDeletingFormId(null); setLoading(false); }
  };

  const handleUploadSponsorForm = async () => {
    if (!newSponsorForm.title || !newSponsorForm.form_pdf) { setError("Title and PDF required"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(newSponsorForm).forEach(([k, v]) => fd.append(k, v));
      await MajangaService.uploadSponsorForm(fd);
      setNewSponsorForm({ title: "", description: "", version: "", is_active: true, form_pdf: null });
      document.getElementById("sponsor-form-upload").value = "";
      await fetchSponsorForms();
      setError(null);
    } catch { setError("Failed to upload sponsor form."); }
    finally { setLoading(false); }
  };

  const handleAdminReview = async (requestId, status, notes) => {
    setLoading(true);
    try {
      await MajangaService.adminReviewRequest(requestId, { status, admin_notes: notes });
      await fetchSponsorRequests(sponsorStatusFilter === "all" ? null : sponsorStatusFilter);
      setSelectedSponsorRequest(null);
      setError(null);
    } catch { setError("Failed to review request."); }
    finally { setLoading(false); }
  };

  const handleSubmitForApproval = async (requestId) => {
    setLoading(true);
    try {
      await MajangaService.submitServiceRequestForApproval(requestId);
      await fetchServiceRequests();
      // Clear cached chain so it reloads
      setApprovalChains(prev => { const n = {...prev}; delete n[requestId]; return n; });
      setError(null);
    } catch { setError("Failed to submit for approval."); }
    finally { setLoading(false); }
  };

  const handleAdminFinalApprove = async (requestId, decision, approvedAmount = null) => {
    setLoading(true);
    try {
      await MajangaService.adminFinalApproveServiceRequest(requestId, {
        decision,
        notes: "",
        ...(approvedAmount ? { approved_amount: parseFloat(approvedAmount) } : {}),
      });
      await fetchServiceRequests();
      setApprovalChains(prev => { const n = {...prev}; delete n[requestId]; return n; });
      setError(null);
    } catch { setError("Failed to process final approval."); }
    finally { setLoading(false); }
  };

  // ── Computed ───────────────────────────────────────────────────────────────
  const filteredGangs = gangs.filter(g =>
    g.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredSponsorRequests = sponsorRequests.filter(r => {
    const q = sponsorSearchTerm.toLowerCase();
    return (
      (r.request_title?.toLowerCase().includes(q) ||
       r.reference_number?.toLowerCase().includes(q) ||
       r.user_info?.name?.toLowerCase().includes(q)) &&
      (sponsorStatusFilter === "all" || r.status === sponsorStatusFilter)
    );
  });

  const pendingCount  = gangRequests.filter(r => r.status === "pending").length;

  // Join requests: filter by status, then group by gang so each community's
  // requests are reviewed together instead of one long mixed list.
  const REQUEST_FILTERS = [
    { id: "all",      label: "All"      },
    { id: "pending",  label: "Pending"  },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
  ];
  const requestCountByStatus = (statusId) =>
    statusId === "all" ? gangRequests.length : gangRequests.filter(r => r.status === statusId).length;

  const visibleRequests = gangRequests.filter(r => {
    const matchesStatus = requestStatusFilter === "all" || r.status === requestStatusFilter;
    const term = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm
      || r.user_name?.toLowerCase().includes(term)
      || r.user_email?.toLowerCase().includes(term)
      || r.gang_name?.toLowerCase().includes(term);
    return matchesStatus && matchesSearch;
  });

  const requestGroups = Object.values(
    visibleRequests.reduce((groups, r) => {
      const key = r.gang ?? r.gang_id ?? r.gang_name ?? "unknown";
      if (!groups[key]) groups[key] = { key, name: r.gang_name || "Unknown community", requests: [] };
      groups[key].requests.push(r);
      return groups;
    }, {})
  ).sort((a, b) => a.name.localeCompare(b.name));
  const totalContribs = gangs.reduce((s, g) => s + (g.total_contribution || 0), 0);
  const totalMembers  = gangs.reduce((s, g) => s + (g.member_count || 0), 0);

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex flex-col h-full min-h-screen bg-slate-50">

      {/* ── Top header with Community Hub branding ──────────────────────── */}
      <div className="bg-white border-b border-slate-100 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
            <Shield size={16} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800 leading-none">Community Hub</p>
            <p className="text-[10px] text-slate-400">Majanga Management System</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Quick stats strip */}
          <div className="hidden md:flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Layers size={12} className="text-teal-500" />{gangs.length} communities</span>
            <span className="flex items-center gap-1"><Users size={12} className="text-blue-500" />{totalMembers} members</span>
            {pendingCount > 0 && (
              <span className="flex items-center gap-1 text-amber-600 font-semibold">
                <Bell size={12} />{pendingCount} pending
              </span>
            )}
          </div>
          <button onClick={fetchGangs} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <RefreshCw size={15} />
          </button>
          <button
            onClick={() => window.location.href = "/general-contributions"}
            title="Open General Contributions"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-xl shadow-sm"
            style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
            <DollarIcon size={13}/> General Contributions
          </button>
          {activeView === "gangs" && (
            <button
              onClick={() => setActiveView("addGang")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-xl shadow-sm"
              style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}
            >
              <Plus size={13} /> New Community
            </button>
          )}
        </div>
      </div>

      {/* ── Scrollable tab bar ──────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100 px-4 flex-shrink-0 overflow-x-auto">
        <div className="flex">
          {tabs.map(t => (
            <Tab
              key={t.id}
              icon={t.icon}
              label={t.label}
              active={activeView === t.id}
              onClick={() => setActiveView(t.id)}
              badge={t.badge}
            />
          ))}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-6 mt-3 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex-shrink-0">
          <AlertTriangle size={15} className="flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}><X size={13} /></button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3 text-teal-600">
            <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto p-6">

        {/* ══ DASHBOARD ════════════════════════════════════════════════════ */}
        {!loading && activeView === "dashboard" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Layers}     label="Communities"          value={gangs.length}      color="navy" />
              <StatCard icon={Users}      label="Total Members"        value={totalMembers}       color="teal" />
              <StatCard icon={Bell}       label="Pending Requests"     value={pendingCount}       color="gold" />
              <StatCard icon={TrendingUp} label="Total Contributions"  value={`TZS ${totalContribs.toLocaleString()}`} sub="all time" color="blue" />
            </div>

            {/* Communities quick list */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-slate-800">Communities Overview</h2>
                  {gangs.length > 6 && (
                    <p className="text-xs text-slate-400 mt-0.5">Showing 6 of {gangs.length} — this is a preview, not the full count</p>
                  )}
                </div>
                <button onClick={() => setActiveView("gangs")} className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                  View all <ChevronRight size={14} />
                </button>
              </div>
              <div className="divide-y divide-slate-50">
                {gangs.slice(0, 6).map(g => (
                  <div key={g.id} className="px-6 py-3.5 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `linear-gradient(135deg, ${TEAL}, #0f766e)` }}>
                      <span className="text-white text-xs font-bold">{g.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{g.name}</p>
                      <p className="text-xs text-slate-400">{g.location}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-slate-700">{g.member_count || 0}</p>
                      <p className="text-xs text-slate-400">members</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${g.is_private ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"}`}>
                      {g.is_private ? "Private" : "Public"}
                    </span>
                  </div>
                ))}
                {gangs.length === 0 && <p className="text-center py-8 text-slate-400 text-sm">No communities yet</p>}
              </div>
            </div>

            {/* Pending requests widget */}
            {pendingCount > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                    <span className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center">
                      <Bell size={11} className="text-amber-600" />
                    </span>
                    Pending Join Requests
                  </h2>
                  <button onClick={() => setActiveView("requests")} className="text-xs text-teal-600 font-medium flex items-center gap-1">
                    View all <ChevronRight size={14} />
                  </button>
                </div>
                <div className="divide-y divide-slate-50">
                  {gangRequests.filter(r => r.status === "pending").slice(0, 5).map(r => (
                    <div key={r.id} className="px-6 py-3.5 flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <User size={14} className="text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{r.user_name}</p>
                        <p className="text-xs text-slate-400">{r.gang_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleApproveRequest(r.id, "approved")} className="p-1.5 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg"><Check size={13} /></button>
                        <button onClick={() => handleApproveRequest(r.id, "rejected")} className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg"><X size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ COMMUNITIES LIST ══════════════════════════════════════════════ */}
        {!loading && activeView === "gangs" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" placeholder="Search communities..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300" />
              </div>
              <span className="text-sm text-slate-400">{filteredGangs.length} communities</span>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {["Community","Location","Type","Members","Contributions","Actions"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredGangs.map(gang => (
                    <tr key={gang.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                            <span className="text-white text-xs font-bold">{gang.name?.[0]?.toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{gang.name}</p>
                            <p className="text-xs text-slate-400 truncate max-w-[140px]">{gang.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        <div className="flex items-center gap-1"><MapPin size={12} className="text-slate-400" />{gang.location?.split(" ").slice(0,3).join(" ")}</div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${gang.is_private ? "bg-red-50 text-red-600":"bg-green-50 text-green-600"}`}>
                          {gang.is_private ? "Private":"Public"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-700">{gang.member_count || 0}</td>
                      <td className="px-5 py-4 text-sm font-semibold text-teal-600">TZS {(gang.total_contribution||0).toLocaleString()}</td>
                      <td className="px-5 py-4">
                        <div className="flex gap-1">
                          <button onClick={() => setEditGang({ ...gang, specification: gang.specification||"All" })} className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg"><Edit size={14} /></button>
                          <button onClick={() => handleDeleteGang(gang.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredGangs.length === 0 && <div className="py-14 text-center text-slate-400"><Layers size={28} className="mx-auto mb-2 opacity-30" /><p className="text-sm">No communities found</p></div>}
            </div>
          </div>
        )}

        {/* ══ ADD COMMUNITY ═══════════════════════════════════════════════ */}
        {!loading && activeView === "addGang" && (
          <div className="max-w-2xl">
            <button onClick={() => setActiveView("gangs")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-5"><ArrowLeft size={15} /> Back</button>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-base font-bold text-slate-800 mb-5">Create New Community</h2>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label:"Community Name", name:"name", placeholder:"Enter name" },
                  { label:"Location", name:"location", placeholder:"City, Country" },
                  { label:"Contact Email", name:"contact_email", placeholder:"email@example.com", type:"email" },
                  { label:"Contact Phone", name:"contact_phone", placeholder:"+255712345678" },
                  { label:"Joining Fee (TZS)", name:"joining_fee", placeholder:"5000.00" },
                ].map(f => (
                  <div key={f.name}>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">{f.label}</label>
                    <input type={f.type||"text"} name={f.name} value={newGang[f.name]}
                      onChange={e => setNewGang({ ...newGang, [e.target.name]: e.target.value })}
                      placeholder={f.placeholder}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Specification</label>
                  <select value={newGang.specification} onChange={e => setNewGang({...newGang, specification: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white">
                    <option value="All">All</option><option value="Majanga">Majanga</option><option value="Cheleko">Cheleko</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                  <textarea name="description" value={newGang.description} onChange={e => setNewGang({...newGang, description: e.target.value})}
                    rows={3} placeholder="Community purpose..." className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none" />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="is_private" checked={newGang.is_private} onChange={e => setNewGang({...newGang, is_private: e.target.checked})} className="w-4 h-4 text-teal-600 rounded" />
                  <label htmlFor="is_private" className="text-sm text-slate-600">Private Community</label>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                <button onClick={() => setActiveView("gangs")} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
                <button onClick={handleAddGang} disabled={loading}
                  className="px-5 py-2 text-sm font-medium text-white rounded-xl hover:opacity-90 disabled:opacity-60"
                  style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                  Create Community
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ MEMBERS ══════════════════════════════════════════════════════ */}
        {!loading && activeView === "members" && (
          <div className="space-y-3">
            <div className="relative max-w-xs">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300" />
            </div>
            {filteredGangs.map(gang => (
              <div key={gang.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <button className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                  onClick={() => gangDetails?.id === gang.id ? setGangDetails(null) : fetchGangDetails(gang.id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                      <span className="text-white text-xs font-bold">{gang.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-slate-800">{gang.name}</p>
                      <p className="text-xs text-slate-400">{gang.member_count||0} members · {gang.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-teal-600">TZS {(gang.total_contribution||0).toLocaleString()}</span>
                    {gangDetails?.id === gang.id ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                  </div>
                </button>
                {gangDetails?.id === gang.id && (
                  <div className="border-t border-slate-100 p-5 space-y-4">
                    {/* Leadership board — only show if any roles assigned */}
                    {gangMemberRoles.filter(r => r.role_type !== "member" && r.is_active !== false).length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Crown size={12} className="text-amber-500"/>
                          Gang Leadership
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {gangMemberRoles
                            .filter(r => r.role_type !== "member" && r.is_active !== false)
                            .sort((a, b) => (a.approval_order||99) - (b.approval_order||99))
                            .map((r, i) => {
                              const ICON_MAP = { chairperson: Crown, secretary: BookOpen, treasurer: DollarIcon, vice_chair: Star, custom: Zap };
                              const COLOR_MAP = {
                                chairperson: "from-amber-400 to-amber-500",
                                secretary:   "from-blue-400 to-blue-500",
                                treasurer:   "from-green-400 to-green-500",
                                vice_chair:  "from-purple-400 to-purple-500",
                                custom:      "from-teal-400 to-teal-500",
                              };
                              const RIcon = ICON_MAP[r.role_type] || Zap;
                              const grad  = COLOR_MAP[r.role_type] || COLOR_MAP.custom;
                              return (
                                <div key={i} className="bg-white rounded-xl border border-slate-100 p-3 flex items-center gap-3 shadow-sm">
                                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                                    <RIcon size={16} className="text-white"/>
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{r.role_name}</p>
                                    <p className="text-xs font-semibold text-slate-800 truncate">{r.membership_name || "—"}</p>
                                  </div>
                                </div>
                              );
                            })
                          }
                        </div>
                      </div>
                    )}

                    {/* Member cards with positions */}
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Users size={12}/>
                        All Members ({activeMembers(gangDetails.members).length})
                      </p>
                    <div className="flex flex-col gap-2">
                      {activeMembers(gangDetails.members).map(m => {
                        const roles = getMemberRoles(m.membership_id || m.id);
                        const ICON_MAP = {
                          chairperson: Crown, secretary: BookOpen, treasurer: DollarIcon,
                          vice_chair: Star, member: User, custom: Zap, admin: Shield,
                        };
                        const COLOR_MAP = {
                          chairperson: "bg-amber-100 text-amber-700 border-amber-200",
                          secretary:   "bg-blue-100 text-blue-700 border-blue-200",
                          treasurer:   "bg-green-100 text-green-700 border-green-200",
                          vice_chair:  "bg-purple-100 text-purple-700 border-purple-200",
                          member:      "bg-slate-100 text-slate-600 border-slate-200",
                          custom:      "bg-teal-100 text-teal-700 border-teal-200",
                          admin:       "bg-rose-100 text-rose-700 border-rose-200",
                        };
                        const mid = m.membership_id || m.id;
                        const expanded = expandedMemberId === mid;
                        return (
                          <div key={m.id} className="bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors overflow-hidden">
                            <button type="button" onClick={() => setExpandedMemberId(expanded ? null : mid)}
                              className="w-full flex items-start gap-3 p-4 text-left">
                              {/* Avatar with photo if attached, else initials */}
                              {m.profile_image_url ? (
                                <img src={m.profile_image_url} alt={m.full_name||"Member"} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-white text-sm"
                                  style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                                  {(m.user?.name || m.full_name || "?")[0].toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                {/* Name */}
                                <p className="text-sm font-semibold text-slate-800 truncate">{m.user?.name || m.full_name || "Unknown"}</p>
                                <p className="text-xs text-slate-400 truncate">{m.user?.email || "N/A"}</p>
                                {/* Join date */}
                                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                                  <Calendar size={10}/> Joined {m.joining_date ? new Date(m.joining_date).toLocaleDateString() : "N/A"}
                                </p>
                                {/* Role badges */}
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {roles.length > 0 ? (
                                    roles.map((r, i) => {
                                      const RIcon = ICON_MAP[r.role_type] || Zap;
                                      const rColor = COLOR_MAP[r.role_type] || COLOR_MAP.custom;
                                      return (
                                        <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${rColor}`}>
                                          <RIcon size={10}/>
                                          {r.role_name}
                                        </span>
                                      );
                                    })
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-slate-400 bg-slate-100 border border-slate-200">
                                      <User size={10}/> No role assigned
                                    </span>
                                  )}
                                </div>
                              </div>
                              {expanded ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0 mt-1"/> : <ChevronDown size={16} className="text-slate-400 flex-shrink-0 mt-1"/>}
                            </button>
                            {expanded && (
                              <div className="px-4 pb-4">
                                <MemberFullDetails m={m} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {activeMembers(gangDetails.members).length === 0 && (
                      <p className="text-sm text-slate-400 text-center py-6">No approved members yet</p>
                    )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ══ CONTRIBUTIONS ════════════════════════════════════════════════ */}
        {!loading && activeView === "contributions" && (
          <div className="space-y-3">
            {gangs.map(gang => (
              <div key={gang.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <button className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                  onClick={() => gangDetails?.id === gang.id ? setGangDetails(null) : fetchGangDetails(gang.id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                      <span className="text-white text-xs font-bold">{gang.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-slate-800">{gang.name}</p>
                      <p className="text-xs text-slate-400">{gang.member_count||0} members</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-bold text-teal-600">TZS {(gang.total_contribution||0).toLocaleString()}</p>
                      <p className="text-xs text-slate-400">Total</p>
                    </div>
                    {gangDetails?.id === gang.id ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                  </div>
                </button>
                {gangDetails?.id === gang.id && (
                  <div className="border-t border-slate-100 p-5">
                    <table className="min-w-full">
                      <thead><tr className="bg-slate-50">{["Member","Email","Total","Avg / Count","Actions"].map(h => <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                      <tbody className="divide-y divide-slate-50">
                        {activeMembers(gangDetails.members).map(m => (
                          <React.Fragment key={m.id}>
                          <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedMemberId(expandedMemberId === (m.membership_id||m.id) ? null : (m.membership_id||m.id))}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
                                  style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                                  {(m.user?.name||"?")[0].toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-slate-800">{m.user?.name||"Unknown"}</p>
                                  <div className="flex flex-wrap gap-1 mt-0.5">
                                    {getMemberRoles(m.membership_id||m.id).length > 0
                                      ? getMemberRoles(m.membership_id||m.id).map((r,i) => {
                                          const CMAP = {chairperson:"bg-amber-100 text-amber-700",secretary:"bg-blue-100 text-blue-700",treasurer:"bg-green-100 text-green-700",vice_chair:"bg-purple-100 text-purple-700",member:"bg-slate-100 text-slate-500",custom:"bg-teal-100 text-teal-700"};
                                          return <span key={i} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${CMAP[r.role_type]||CMAP.custom}`}>{r.role_name}</span>;
                                        })
                                      : <span className="text-[10px] text-slate-400">Member</span>
                                    }
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">{m.user?.email||"N/A"}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-teal-600">TZS {m.contribution_statistics?.total?.toLocaleString()||0}</td>
                            <td className="px-4 py-3 text-sm text-slate-500">TZS {m.contribution_statistics?.average?.toLocaleString()||0} ({m.contribution_statistics?.count||0})</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                <button onClick={() => { setNewContribution({ amount:"", description:"", memberId: m.membership_id, gangId: gang.id, contribution_date: new Date().toISOString().split("T")[0], is_joining_fee: false }); setShowContributionModal(true); }}
                                  className="px-3 py-1.5 text-xs font-medium text-white rounded-lg" style={{ background: NAV }}>Add</button>
                                <button onClick={() => { fetchContributionHistory(m.membership_id); setShowHistoryModal(true); }}
                                  className="px-3 py-1.5 text-xs font-medium text-white bg-teal-500 hover:bg-teal-600 rounded-lg">History</button>
                                <button onClick={() => setExpandedMemberId(expandedMemberId === (m.membership_id||m.id) ? null : (m.membership_id||m.id))}
                                  className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg" title="View joining form details">
                                  {expandedMemberId === (m.membership_id||m.id) ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedMemberId === (m.membership_id||m.id) && (
                            <tr><td colSpan="5" className="px-4 py-4 bg-slate-50">
                              {/* The membership form fields below are already returned by the
                                  API (full_name, phone, address, family, kin/emergency contacts,
                                  medical, contribution, banking, witness, photo - everything the
                                  CFN/joining form collects) but were never rendered anywhere -
                                  the data wasn't lost, it just wasn't shown. */}
                              <MemberFullDetails m={m} />
                            </td></tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ══ INDIVIDUAL CFN ═══════════════════════════════════════════════ */}
        {!loading && activeView === "individual" && (
          <div className="space-y-4">
            <div className="relative max-w-xs">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" placeholder="Search CFN forms..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300" />
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <table className="min-w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-100">{["CEFN No.","Name","Email","Location","Date",""].map(h => <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {(cfnform.results||[]).filter(f => f.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || f.cefn_no?.toLowerCase().includes(searchTerm.toLowerCase())).map(form => (
                    <React.Fragment key={form.id}>
                      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedCFNForm(selectedCFNForm === form.id ? null : form.id)}>
                        <td className="px-5 py-3.5"><span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded-lg">{form.cefn_no}</span></td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            {form.profile_image ? <img src={form.profile_image} className="w-8 h-8 rounded-full object-cover" alt="" /> : <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><User size={14} className="text-slate-400" /></div>}
                            <div>
                              <p className="text-sm font-medium text-slate-800">{form.full_name}</p>
                              <p className="text-xs text-slate-400">{form.user?.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-500">{form.prepared_by_email}</td>
                        <td className="px-5 py-3.5 text-sm text-slate-500">{form.current_domicile_address?.split(",")[0]}</td>
                        <td className="px-5 py-3.5 text-sm text-slate-500">{new Date(form.created_at).toLocaleDateString()}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <button
                              title="Delete this member's CFN form"
                              onClick={(e) => { e.stopPropagation(); handleDeleteCFNForm(form.id, form.full_name); }}
                              disabled={deletingFormId === form.id}
                              className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg disabled:opacity-40"
                            >
                              <Trash2 size={13} />
                            </button>
                            {selectedCFNForm===form.id ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
                          </div>
                        </td>
                      </tr>
                      {selectedCFNForm === form.id && (
                        <tr><td colSpan="6" className="px-5 py-4 bg-slate-50">
                          {/* Group & Membership — a CFN form has no gang of its own (by
                              design), but the same person is very often also a group
                              member; this is a plain cross-reference by user, so admins
                              can see that link without it being lost. */}
                          <div className="bg-white rounded-xl p-3.5 border border-slate-100 mb-3">
                            <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 pb-1 border-b border-slate-100">Group &amp; Membership</p>
                            {(form.gang_memberships||[]).length > 0 ? (
                              <div className="space-y-2">
                                {form.gang_memberships.map((gm, i) => (
                                  <div key={i} className="flex items-center justify-between gap-3 text-xs">
                                    <div>
                                      <span className="font-medium text-slate-700">{gm.gang_name}</span>
                                      <span className="text-slate-400 ml-2">({gm.gang_specification})</span>
                                    </div>
                                    <StatusBadge status={gm.status} />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-400">Not a member of any community group</p>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {[
                              { title:"Personal", data:[["Spouse",form.spouse_name],["Birthday",form.birthday_date?new Date(form.birthday_date).toLocaleDateString():"N/A"],["Joining",form.joining_date?new Date(form.joining_date).toLocaleDateString():"N/A"]] },
                              { title:"Address", data:[["Business",form.business_address],["Current",form.current_domicile_address],["Permanent",form.perm_domicile_address]] },
                              { title:"Family", data:[["Father",form.father_name],["Mother",form.mother_name],["Father-in-Law",form.father_in_law]] },
                              { title:"Medical", data:[["Checkup",form.medical_checkup?"Yes":"No"],["Insurance",form.health_insurance?"Yes":"No"],["ENF",form.medical_checkup_financed_by_enf?"Yes":"No"]] },
                              { title:"Contribution", data:[["Amt 1",`TZS ${parseFloat(form.contribution_amount1||0).toLocaleString()}`],["Amt 2",`TZS ${parseFloat(form.contribution_amount2||0).toLocaleString()}`],["Target",`TZS ${parseFloat(form.contribution_total||0).toLocaleString()}`]] },
                              { title:"Witness", data:[["Name",form.witness_name],["Title",form.witness_designation],["Phone",form.witness_phone]] },
                            ].map(s => (
                              <div key={s.title} className="bg-white rounded-xl p-3.5 border border-slate-100">
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 pb-1 border-b border-slate-100">{s.title}</p>
                                {s.data.map(([l,v]) => (
                                  <div key={l} className="flex justify-between gap-2 py-0.5">
                                    <span className="text-xs text-slate-400">{l}</span>
                                    <span className="text-xs font-medium text-slate-700 text-right truncate max-w-[110px]">{v||"N/A"}</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-end mt-3">
                            <button onClick={() => handleDeleteCFNForm(form.id, form.full_name)} disabled={deletingFormId===form.id}
                              className="flex items-center gap-2 px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-xl disabled:opacity-50">
                              <Trash2 size={13} />{deletingFormId===form.id?"Deleting...":"Delete Form"}
                            </button>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ JOIN REQUESTS ════════════════════════════════════════════════ */}
        {!loading && activeView === "requests" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative max-w-xs">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" placeholder="Search requests..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300" />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {REQUEST_FILTERS.map(f => {
                  const active = requestStatusFilter === f.id;
                  return (
                    <button key={f.id} onClick={() => setRequestStatusFilter(f.id)}
                      className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        active ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}>
                      {f.label}
                      <span className={`min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                        active ? "bg-white text-slate-800" : "bg-slate-100 text-slate-500"}`}>
                        {requestCountByStatus(f.id)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {requestGroups.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 py-14 text-center text-slate-400">
                <Bell size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">
                  {gangRequests.length === 0 ? "No join requests" : "No requests match this filter"}
                </p>
              </div>
            ) : requestGroups.map(group => {
              const groupPending = group.requests.filter(r => r.status === "pending").length;
              return (
                <div key={group.key} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                        <span className="text-white text-xs font-bold">{group.name?.[0]?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{group.name}</p>
                        <p className="text-xs text-slate-400">
                          {group.requests.length} request{group.requests.length === 1 ? "" : "s"}
                          {groupPending > 0 && ` · ${groupPending} pending`}
                        </p>
                      </div>
                    </div>
                    {groupPending > 0 && (
                      <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                        {groupPending} to review
                      </span>
                    )}
                  </div>
                  <table className="min-w-full">
                    <thead><tr className="bg-white border-b border-slate-100">{["Member","Date","Status","Actions"].map(h=><th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {group.requests.map(req => (
                        <tr key={req.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><User size={13} className="text-slate-500"/></div>
                              <div>
                                <p className="text-sm font-medium text-slate-800">{req.user_name}</p>
                                <p className="text-xs text-slate-400">{req.user_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-sm text-slate-500">{new Date(req.created_at).toLocaleDateString()}</td>
                          <td className="px-5 py-3.5"><StatusBadge status={req.status} /></td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => { setSelectedRequest(req); setActiveView("requestDetails"); }} className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg"><Eye size={13}/></button>
                              <button onClick={() => openApproveModal(req)} disabled={req.status==="approved"} className="p-1.5 hover:bg-green-50 text-green-500 rounded-lg disabled:opacity-30" title="Approve &amp; assign role"><Check size={13}/></button>
                              <button onClick={() => handleRejectRequest(req.id)} disabled={req.status==="rejected"} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg disabled:opacity-30"><X size={13}/></button>
                              {req.status === "rejected" && (
                                <button onClick={() => handleDeleteRequest(req.id)} className="p-1.5 hover:bg-red-100 text-red-700 rounded-lg" title="Delete permanently"><Trash2 size={13}/></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}

        {/* ══ REQUEST DETAIL ═══════════════════════════════════════════════ */}
        {!loading && activeView === "requestDetails" && selectedRequest && (
          <div className="max-w-2xl space-y-4">
            <button onClick={() => setActiveView("requests")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft size={15}/> Back</button>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-5">
              <div className="flex items-start justify-between">
                <h2 className="text-base font-bold text-slate-800">Request Details</h2>
                <StatusBadge status={selectedRequest.status} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Member</p>
                  <div className="flex items-center gap-2 text-sm text-slate-700 mb-1"><User size={14} className="text-slate-400"/>{selectedRequest.user_name}</div>
                  <div className="flex items-center gap-2 text-sm text-slate-500"><Mail size={14} className="text-slate-400"/>{selectedRequest.user_email}</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Community</p>
                  <div className="flex items-center gap-2 text-sm text-slate-700 mb-1"><Layers size={14} className="text-slate-400"/>{selectedRequest.gang_name}</div>
                  <div className="flex items-center gap-2 text-sm text-slate-500"><Calendar size={14} className="text-slate-400"/>{new Date(selectedRequest.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              {selectedRequest.status === "pending" && (
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                  <button onClick={() => handleRejectRequest(selectedRequest.id)} className="flex items-center gap-2 px-4 py-2 text-sm border border-red-300 text-red-600 rounded-xl hover:bg-red-50"><X size={14}/>Reject</button>
                  <button onClick={() => openApproveModal(selectedRequest)} className="flex items-center gap-2 px-4 py-2 text-sm bg-green-500 hover:bg-green-600 text-white rounded-xl"><Check size={14}/>Approve & Assign Role</button>
                </div>
              )}
              {selectedRequest.status === "rejected" && (
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                  <button onClick={() => handleDeleteRequest(selectedRequest.id)} className="flex items-center gap-2 px-4 py-2 text-sm border border-red-300 text-red-700 rounded-xl hover:bg-red-50"><Trash2 size={14}/>Delete Permanently</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ SERVICE REQUESTS WITH APPROVAL CHAINS ══════════════════════ */}
        {!loading && activeView === "serviceRequests" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative max-w-xs">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" placeholder="Search service requests..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300" />
              </div>
              <button onClick={fetchServiceRequests} className="p-2 hover:bg-slate-100 rounded-xl text-slate-500"><RefreshCw size={15}/></button>
            </div>

            <div className="space-y-3">
              {(Array.isArray(serviceRequests) ? serviceRequests : []).filter(r =>
                !searchTerm ||
                r.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                r.reference_number?.toLowerCase().includes(searchTerm.toLowerCase())
              ).map(req => {
                const chain        = approvalChains[req.id] || [];
                const isExpanded   = selectedSponsorRequest === req.id;
                const chainLoading = loadingChain === req.id;

                return (
                  <div key={req.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    {/* Row */}
                    <div
                      className="px-5 py-4 flex items-start gap-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => {
                        if (isExpanded) {
                          setSelectedSponsorRequest(null);
                        } else {
                          setSelectedSponsorRequest(req.id);
                          fetchApprovalChain(req.id);
                        }
                      }}
                    >
                      {/* Left: info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg">{req.reference_number}</span>
                          <StatusBadge status={req.status} />
                          {req.approval_chain_status && req.approval_chain_status !== "not_started" && (
                            <StatusBadge status={req.approval_chain_status} />
                          )}
                        </div>
                        <p className="text-sm font-semibold text-slate-800">{req.title}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span className="flex items-center gap-1"><User size={11}/>{req.user?.name || "—"}</span>
                          {req.gang_details && <span className="flex items-center gap-1"><Layers size={11}/>{req.gang_details.name}</span>}
                          <span className="flex items-center gap-1"><Calendar size={11}/>{new Date(req.request_date || req.created_at).toLocaleDateString()}</span>
                          {req.requested_amount && <span className="flex items-center gap-1 font-semibold text-teal-600"><DollarIcon size={11}/>TZS {parseFloat(req.requested_amount).toLocaleString()}</span>}
                        </div>

                        {/* Approval chain mini-progress — always visible if chain exists */}
                        {req.approval_chain_status && req.approval_chain_status !== "not_started" && chain.length > 0 && (
                          <div className="mt-3">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Approval Progress — Step {req.current_approval_step}/{chain.length}</p>
                            <ApprovalChainProgress chain={chain} currentStep={req.current_approval_step} chainStatus={req.approval_chain_status} />
                          </div>
                        )}
                      </div>

                      {/* Right: chevron */}
                      <div className="flex-shrink-0 flex items-center gap-2">
                        {req.status === "pending" && req.approval_chain_status === "not_started" && (
                          <button
                            onClick={e => { e.stopPropagation(); handleSubmitForApproval(req.id); }}
                            className="px-3 py-1.5 text-xs font-semibold text-white rounded-xl"
                            style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}
                          >
                            Start Approval
                          </button>
                        )}
                        {isExpanded ? <ChevronUp size={16} className="text-slate-400"/> : <ChevronDown size={16} className="text-slate-400"/>}
                      </div>
                    </div>

                    {/* Expanded approval detail */}
                    {isExpanded && (
                      <div className="border-t border-slate-100 bg-slate-50 p-5 space-y-4">

                        {chainLoading && (
                          <div className="flex items-center gap-2 text-sm text-teal-600">
                            <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"/>
                            Loading approval chain...
                          </div>
                        )}

                        {/* Full chain steps */}
                        {chain.length > 0 && (
                          <div>
                            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">Approval Chain</p>
                            <div className="space-y-2">
                              {chain.map((step, i) => {
                                const RoleIcon = ROLE_ICON[step.gang_role?.role_type || (step.approver_type === "admin" ? "admin" : "custom")] || Shield;
                                const roleColor = ROLE_COLOR[step.gang_role?.role_type || (step.approver_type === "admin" ? "admin" : "custom")] || ROLE_COLOR.custom;
                                const isCurrent = step.step_number === req.current_approval_step && !step.decision;
                                return (
                                  <div key={step.id || i}
                                    className={`flex items-center gap-3 p-3.5 rounded-xl border ${
                                      step.decision === "approved" ? "border-emerald-200 bg-emerald-50" :
                                      step.decision === "rejected" ? "border-red-200 bg-red-50" :
                                      isCurrent ? "border-amber-200 bg-amber-50" :
                                      "border-slate-200 bg-white"
                                    }`}
                                  >
                                    {/* Step number */}
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                      step.decision === "approved" ? "bg-emerald-500 text-white" :
                                      step.decision === "rejected" ? "bg-red-500 text-white" :
                                      isCurrent ? "bg-amber-400 text-white" :
                                      "bg-slate-200 text-slate-500"
                                    }`}>
                                      {step.decision === "approved" ? <Check size={12}/> :
                                       step.decision === "rejected" ? <X size={12}/> :
                                       step.step_number}
                                    </div>

                                    {/* Role badge */}
                                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold flex-shrink-0 ${roleColor}`}>
                                      <RoleIcon size={12}/>
                                      {step.gang_role_name || (step.approver_type === "admin" ? "System Admin" : "Unknown Role")}
                                    </div>

                                    {/* Approver name */}
                                    <div className="flex-1 min-w-0">
                                      {step.approver_name ? (
                                        <p className="text-sm font-medium text-slate-700 truncate">{step.approver_name}</p>
                                      ) : (
                                        <p className="text-sm text-slate-400 italic">No member assigned</p>
                                      )}
                                      {isCurrent && <p className="text-[10px] text-amber-600 font-semibold">⏳ Awaiting decision</p>}
                                      {step.decided_at && <p className="text-[10px] text-slate-400">{new Date(step.decided_at).toLocaleDateString()}</p>}
                                    </div>

                                    {/* Decision badge */}
                                    {step.decision && <StatusBadge status={step.decision}/>}

                                    {/* Notes */}
                                    {step.notes && <p className="text-xs text-slate-500 italic max-w-[160px] truncate" title={step.notes}>"{step.notes}"</p>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {chain.length === 0 && !chainLoading && req.approval_chain_status !== "not_started" && (
                          <p className="text-sm text-slate-400 italic">No approval steps found.</p>
                        )}

                        {/* Request description */}
                        {req.description && (
                          <div className="bg-white rounded-xl p-4 border border-slate-100">
                            <p className="text-xs font-bold text-slate-500 uppercase mb-1.5">Description</p>
                            <p className="text-sm text-slate-600">{req.description}</p>
                          </div>
                        )}

                        {/* Admin final approve (if pending_admin) */}
                        {req.approval_chain_status === "pending_admin" && (
                          <div className="flex justify-end gap-3 pt-2">
                            <p className="flex-1 text-xs text-slate-500 flex items-center gap-1">
                              <Shield size={12} className="text-teal-500"/>
                              All officer approvals complete — awaiting your final decision
                            </p>
                            <button onClick={() => handleAdminFinalApprove(req.id, "rejected")}
                              className="flex items-center gap-2 px-4 py-2 text-sm border border-red-300 text-red-600 rounded-xl hover:bg-red-50">
                              <XCircle size={14}/> Reject
                            </button>
                            <button onClick={() => {
                              const amt = prompt("Enter approved amount (TZS):");
                              if (amt !== null) handleAdminFinalApprove(req.id, "approved", amt);
                            }}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-xl"
                              style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                              <CheckCircle size={14}/> Final Approve
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {serviceRequests.length === 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 py-16 text-center text-slate-400">
                  <Shield size={32} className="mx-auto mb-3 opacity-20"/>
                  <p className="text-sm">No service requests found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ SPONSOR FORMS ════════════════════════════════════════════════ */}
        {!loading && activeView === "sponsorForms" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <h2 className="text-base font-bold text-slate-800 mb-5">Upload Sponsor Form</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Form Title *</label>
                  <input type="text" value={newSponsorForm.title} onChange={e => setNewSponsorForm({...newSponsorForm, title: e.target.value})} placeholder="e.g., General Sponsorship Form 2025"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Version</label>
                  <input type="text" value={newSponsorForm.version} onChange={e => setNewSponsorForm({...newSponsorForm, version: e.target.value})} placeholder="1.0"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"/>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                  <textarea value={newSponsorForm.description} onChange={e => setNewSponsorForm({...newSponsorForm, description: e.target.value})} rows={2}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"/>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Upload PDF *</label>
                  <input id="sponsor-form-upload" type="file" accept="application/pdf"
                    onChange={e => { const f = e.target.files[0]; if (f?.type==="application/pdf") setNewSponsorForm({...newSponsorForm, form_pdf: f}); else setError("PDF only"); }}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"/>
                  {newSponsorForm.form_pdf && <p className="text-xs text-teal-600 mt-1">✓ {newSponsorForm.form_pdf.name}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="is_active_s" checked={newSponsorForm.is_active} onChange={e => setNewSponsorForm({...newSponsorForm, is_active: e.target.checked})} className="w-4 h-4 text-teal-600 rounded"/>
                  <label htmlFor="is_active_s" className="text-xs text-slate-600">Active (available for download)</label>
                </div>
              </div>
              <div className="flex justify-end mt-5">
                <button onClick={handleUploadSponsorForm} disabled={loading}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-xl hover:opacity-90 disabled:opacity-60"
                  style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                  <Upload size={14}/> Upload Form
                </button>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100"><h2 className="font-semibold text-slate-800">Available Forms ({sponsorForms.length})</h2></div>
              <table className="min-w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-100">{["Title","Version","Uploaded By","Downloads","Status","Actions"].map(h=><th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {sponsorForms.map(form => (
                    <tr key={form.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3.5"><p className="text-sm font-medium text-slate-800">{form.title}</p><p className="text-xs text-slate-400">{form.description}</p></td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{form.version}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{form.uploaded_by_info?.name||"N/A"}</td>
                      <td className="px-5 py-3.5 text-sm font-semibold text-slate-700">{form.download_count}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={form.is_active?"active":"inactive"}/></td>
                      <td className="px-5 py-3.5">
                        <div className="flex gap-1">
                          <button onClick={() => { MajangaService.trackFormDownload(form.id); window.open(form.form_pdf_url,"_blank"); }} className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg"><Download size={14}/></button>
                          <button onClick={async () => { if (window.confirm("Delete this form?")) { await MajangaService.deleteSponsorForm(form.id); fetchSponsorForms(); }}} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sponsorForms.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No sponsor forms yet</div>}
            </div>
          </div>
        )}

        {/* ══ SPONSOR REQUESTS ═════════════════════════════════════════════ */}
        {!loading && activeView === "sponsorRequests" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative max-w-xs">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="text" placeholder="Search requests..." value={sponsorSearchTerm} onChange={e => setSponsorSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"/>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Filter size={14} className="text-slate-400"/>
                <select value={sponsorStatusFilter} onChange={e => setSponsorStatusFilter(e.target.value)}
                  className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300">
                  <option value="all">All Status</option>
                  {["draft","submitted","approved","rejected"].map(s=><option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <table className="min-w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-100">{["Reference","Title","Requester","Sponsors","Rate","Status",""].map(h=><th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredSponsorRequests.map(req => (
                    <React.Fragment key={req.id}>
                      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedSponsorRequest(selectedSponsorRequest===req.id?null:req.id)}>
                        <td className="px-5 py-3.5"><span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded-lg">{req.reference_number}</span></td>
                        <td className="px-5 py-3.5 text-sm font-medium text-slate-800">{req.request_title}</td>
                        <td className="px-5 py-3.5"><p className="text-sm font-medium text-slate-800">{req.user_info?.name}</p><p className="text-xs text-slate-400">{req.user_info?.email}</p></td>
                        <td className="px-5 py-3.5">
                          <div className="flex gap-2 text-xs">
                            <span className="text-green-600 font-semibold">{req.accepted_count}✓</span>
                            <span className="text-amber-500">{req.pending_count}⏱</span>
                            <span className="text-red-500">{req.rejected_count}✗</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-100 rounded-full h-1.5 w-16">
                              <div className="bg-teal-500 h-1.5 rounded-full" style={{ width: `${req.acceptance_rate||0}%` }}/>
                            </div>
                            <span className="text-xs font-medium text-slate-600">{req.acceptance_rate?.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5"><StatusBadge status={req.status}/></td>
                        <td className="px-5 py-3.5">{selectedSponsorRequest===req.id?<ChevronUp size={15}/>:<ChevronDown size={15}/>}</td>
                      </tr>
                      {selectedSponsorRequest===req.id && (
                        <tr><td colSpan="7" className="px-5 py-5 bg-slate-50">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="bg-white rounded-xl p-4 border border-slate-100">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Description</p>
                              <p className="text-sm text-slate-600">{req.request_description||"No description"}</p>
                            </div>
                            <div className="bg-white rounded-xl p-4 border border-slate-100">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Sponsor Responses</p>
                              {req.sponsorship_responses?.map((r,i) => (
                                <div key={i} className="flex items-center justify-between py-1">
                                  <span className="text-sm text-slate-700">{r.sponsor_info?.name}</span>
                                  <StatusBadge status={r.status}/>
                                </div>
                              ))}
                              {!req.sponsorship_responses?.length && <p className="text-xs text-slate-400">No responses yet</p>}
                            </div>
                          </div>
                          {req.status==="submitted" && (
                            <div className="flex justify-end gap-3">
                              <button onClick={() => { const n=prompt("Rejection notes:"); if (n) handleAdminReview(req.id,"rejected",n); }}
                                className="flex items-center gap-2 px-4 py-2 text-sm border border-red-300 text-red-600 rounded-xl hover:bg-red-50"><XCircle size={14}/>Reject</button>
                              <button onClick={() => { const n=prompt("Approval notes (optional):")||""; handleAdminReview(req.id,"approved",n); }}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-xl" style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}><CheckCircle size={14}/>Approve</button>
                            </div>
                          )}
                          {req.filled_form_url && (
                            <button onClick={() => window.open(req.filled_form_url,"_blank")}
                              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-500 text-white rounded-xl mt-2"><FileText size={14}/>View Filled Form</button>
                          )}
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {filteredSponsorRequests.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No sponsor requests found</div>}
            </div>
          </div>
        )}

        {/* ══ CONFIGURATION ════════════════════════════════════════════════ */}
        {activeView === "configuration" && (
          <MajangaConfiguration gangs={gangs} onRefresh={fetchGangs} />
        )}
      </main>

      {/* ── EDIT GANG MODAL ─────────────────────────────────────────────── */}
      {editGang.id && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-800">Edit Community</h3>
              <button onClick={() => setEditGang({id:null})} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16}/></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              {[{label:"Name",key:"name"},{label:"Location",key:"location"},{label:"Contact Email",key:"contact_email",type:"email"},{label:"Contact Phone",key:"contact_phone"},{label:"Joining Fee (TZS)",key:"joining_fee"}].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">{f.label}</label>
                  <input type={f.type||"text"} value={editGang[f.key]||""} onChange={e => setEditGang({...editGang,[f.key]:e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"/>
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Specification</label>
                <select value={editGang.specification||"All"} onChange={e => setEditGang({...editGang,specification:e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white">
                  <option value="All">All</option><option value="Majanga">Majanga</option><option value="Cheleko">Cheleko</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                <textarea value={editGang.description||""} onChange={e => setEditGang({...editGang,description:e.target.value})} rows={3}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"/>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="ep" checked={editGang.is_private||false} onChange={e => setEditGang({...editGang,is_private:e.target.checked})} className="w-4 h-4 text-teal-600 rounded"/>
                <label htmlFor="ep" className="text-sm text-slate-600">Private Community</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
              <button onClick={() => setEditGang({id:null})} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
              <button onClick={handleUpdateGang} disabled={loading}
                className="px-5 py-2 text-sm font-medium text-white rounded-xl hover:opacity-90 disabled:opacity-60"
                style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD CONTRIBUTION MODAL ──────────────────────────────────────── */}
      {showContributionModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-800">Add Contribution</h3>
              <button onClick={() => setShowContributionModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16}/></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Amount (TZS) *</label>
                <input type="number" placeholder="Enter amount" value={newContribution.amount} onChange={e => setNewContribution({...newContribution,amount:e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                <textarea rows={3} placeholder="Optional description" value={newContribution.description} onChange={e => setNewContribution({...newContribution,description:e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"/>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
              <button onClick={() => setShowContributionModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
              <button onClick={handleAddContribution} disabled={loading||!newContribution.amount}
                className="px-5 py-2 text-sm font-medium text-white rounded-xl hover:opacity-90 disabled:opacity-50"
                style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                Add Contribution
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── APPROVE MEMBERSHIP MODAL ─── role picker ─────────────────────── */}
      {approveModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h3 className="text-base font-bold text-slate-800">Approve Membership Request</h3>
                <p className="text-xs text-slate-400 mt-0.5">{approveModal.request.gang_name}</p>
              </div>
              <button onClick={() => setApproveModal(null)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16}/></button>
            </div>

            <div className="p-6 space-y-5">

              {/* Member info */}
              <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">
                    {approveModal.request.user_name?.[0]?.toUpperCase() || "?"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{approveModal.request.user_name}</p>
                  <p className="text-xs text-slate-400">{approveModal.request.user_email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-slate-500">Joining</p>
                  <p className="text-sm font-semibold text-slate-700">{approveModal.request.gang_name}</p>
                </div>
              </div>

              {/* Role selector */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Assign Role
                  <span className="ml-2 text-xs font-normal text-slate-400">(optional — based on gang configuration)</span>
                </label>

                {approveModalLoading ? (
                  <div className="flex items-center gap-2 text-sm text-teal-600 py-3">
                    <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"/>
                    Loading roles...
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* No role option */}
                    <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedRoleId === "" ? "border-teal-400 bg-teal-50" : "border-slate-100 hover:border-slate-300"
                    }`}>
                      <input type="radio" name="role" value="" checked={selectedRoleId === ""} onChange={() => setSelectedRoleId("")} className="sr-only"/>
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <User size={15} className="text-slate-400"/>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">Regular Member</p>
                        <p className="text-xs text-slate-400">Approve without assigning a special role</p>
                      </div>
                      {selectedRoleId === "" && <Check size={16} className="text-teal-500 flex-shrink-0"/>}
                    </label>

                    {/* Role options from gang config */}
                    {approveModal.gangRoles
                      .filter(r => r.is_active)
                      .map(role => {
                        const ICON_MAP = {
                          chairperson: Crown,
                          secretary:   BookOpen,
                          treasurer:   DollarIcon,
                          vice_chair:  Star,
                          member:      User,
                          custom:      Zap,
                        };
                        const COLOR_MAP = {
                          chairperson: "bg-amber-100 text-amber-600",
                          secretary:   "bg-blue-100 text-blue-600",
                          treasurer:   "bg-green-100 text-green-600",
                          vice_chair:  "bg-purple-100 text-purple-600",
                          member:      "bg-slate-100 text-slate-500",
                          custom:      "bg-teal-100 text-teal-600",
                        };
                        const RIcon = ICON_MAP[role.role_type] || Zap;
                        const rColor = COLOR_MAP[role.role_type] || COLOR_MAP.custom;
                        const isSelected = selectedRoleId === String(role.id);
                        return (
                          <label key={role.id} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            isSelected ? "border-teal-400 bg-teal-50" : "border-slate-100 hover:border-slate-300"
                          }`}>
                            <input type="radio" name="role" value={role.id} checked={isSelected} onChange={() => setSelectedRoleId(String(role.id))} className="sr-only"/>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${rColor}`}>
                              <RIcon size={15}/>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-slate-800">{role.name}</p>
                                {role.can_approve_service_requests && (
                                  <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-semibold">Approver</span>
                                )}
                                {role.approval_order && (
                                  <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Step {role.approval_order}</span>
                                )}
                              </div>
                              {role.description && <p className="text-xs text-slate-400 mt-0.5">{role.description}</p>}
                            </div>
                            {isSelected && <Check size={16} className="text-teal-500 flex-shrink-0"/>}
                          </label>
                        );
                      })
                    }

                    {approveModal.gangRoles.filter(r => r.is_active).length === 0 && (
                      <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <AlertTriangle size={15} className="text-amber-500 flex-shrink-0 mt-0.5"/>
                        <div>
                          <p className="text-xs font-medium text-amber-700">No roles configured for this gang</p>
                          <p className="text-xs text-amber-600">Go to Configuration → select this gang → Init Default Roles to set up roles first.</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Optional notes */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Notes <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea
                  value={approveNotes}
                  onChange={e => setApproveNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. Elected at the annual meeting on 12 March..."
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
              <button onClick={() => setApproveModal(null)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
              <button
                onClick={handleConfirmApprove}
                disabled={loading}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-xl hover:opacity-90 disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #001f3f, #0d9488)" }}
              >
                <Check size={15}/>
                {selectedRoleId
                  ? `Approve as ${approveModal.gangRoles.find(r => String(r.id) === selectedRoleId)?.name || "Member"}`
                  : "Approve as Regular Member"
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CONTRIBUTION HISTORY MODAL ──────────────────────────────────── */}
      {showHistoryModal && contributionHistory && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <div>
                <h3 className="text-base font-bold text-slate-800">Contribution History</h3>
                <p className="text-xs text-slate-400">{contributionHistory.member_name}</p>
              </div>
              <button onClick={() => { setShowHistoryModal(false); setContributionHistory(null); }} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16}/></button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { label:"Total",    value: contributionHistory?.statistics?.total_contribution??0 },
                  { label:"Regular",  value: contributionHistory?.statistics?.regular_contributions_total??0 },
                  { label:"Average",  value: contributionHistory?.statistics?.average??0 },
                  { label:"Join Fee", value: contributionHistory?.statistics?.joining_fee_total??0 },
                ].map(s => (
                  <div key={s.label} className="bg-slate-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-slate-500 mb-1">{s.label}</p>
                    <p className="text-sm font-bold text-slate-800">TZS {s.value.toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <table className="min-w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-100">{["Date","Amount","Type","Description","Added By"].map(h=><th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {contributionHistory.contributions?.map(c => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-500">{new Date(c.contribution_date).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-teal-600">TZS {parseFloat(c.amount).toLocaleString()}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.is_joining_fee?"active":"pending"}/></td>
                      <td className="px-4 py-3 text-sm text-slate-500">{c.description||"—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{c.user?.name||"System"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Majanga;